import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CollapsibleCardRow, CollapsibleContainer, Container, Grid } from './skill.styles';
import { SkillLayoutSkeleton } from './skill.skeleton';
import { ActionCard, Button, ChevronIcon, Collapsible, CollapsibleStep, FocusPanel, FormControl, KeplerPoints, MapIcon, PageHeader, randomColours, RequiredValidator, SearchIcon, SkillPercentage, Textarea, useCascadingPanelConnector } from '@keplerco/core';
import { ISkillLayoutProps } from './skill.models';
import { DefaultOptions, useApis, useSetFetchState } from '../../global-state';
import { FindSingleSkillDto, SearchSkillDto, TopicLevelDto } from '../../apis';
import { PageLoaderLayer } from '../../components/loading-handling/loader-layers/page-loader-layer/page-loader-layer';
import { PagePath } from '../../navigation/navigation.enums';
import { useForm } from 'react-hook-form';
import { LessonCard } from '../../components/lesson-card/lesson-card';
import { ILessonCardFooterProps, ILessonCardProps } from '../../components/lesson-card/lesson-card.models';
import { AssessmentState } from '../../enums/assessmentState';
import classNames from 'classnames';
import { FetchStatus, FetchType } from '../../enums/fetch';

interface IFormValues {
  prompt: string;
}
export function SkillLayout(props: ISkillLayoutProps): JSX.Element {
  const navigate = useNavigate();

  const setFetchState = useSetFetchState();


  const { topicApi, skillApi } = useApis();
  const [topics, setTopics] = useState<Array<TopicLevelDto>>();

  const [skill, setSkill] = useState<FindSingleSkillDto>();

  const { handleSubmit, control, setValue } = useForm<any>({ mode: 'onChange' });
  const [cantFind, setCantFind] = useState<boolean>(false);

  async function getPageData(prompt: string | undefined) {
    if (!props.skillId) return;
    skillApi.skillSkillIdGet(props.skillId, DefaultOptions).then(x => {
      setSkill(x)
    })
    setFetchState(FetchStatus.Active, FetchType.PageFetching);
    topicApi.topicSuggestGet(props.skillId, undefined, prompt, DefaultOptions)
      .then(x => {
        setTopics(x);
        setFetchState(FetchStatus.Inactive, FetchType.PageFetching);
      });
  }

  useEffect(() => {
    getPageData('');
  }, [props.skillId]);

  async function onSubmitHandler(values: IFormValues) {
    setCantFind(false);
    await getPageData(values.prompt);
  }

  return (
    <React.Fragment>
      <PageLoaderLayer path={PagePath.skills} skeletonLoader={<SkillLayoutSkeleton />}>

        {!!skill &&
          <div className="wrapper stack">
            <PageHeader
              breadcrumbs={[
                {
                  name: 'Skills',
                  url: PagePath.skills
                },
                {
                  name: skill.name!,
                  url: window.location.pathname,
                },
              ]}
              title={skill.name!}
              subtitle={skill.description!}
            />
            <Grid>
              <Container className="card glass">
                <SkillPercentage
                  percentage={skill.score!}
                  notAssessed={!skill.score}
                  barColour={'baby-blue'}
                  title="Overall Quiz Score"
                />
              </Container>
              <ActionCard
                icon={() => <MapIcon />}
                actionText="Find more"
                description={`Tell us about what you would like to learn related to this skill, and we'll help you find better topics.`}
                onClick={() => {
                  setCantFind(true);
                }}
                title="Can't find topics you're interested in?"
              />
            </Grid>

            {topics != undefined && topics?.length > 0 && (
              <CollapsibleContainer>
                <Collapsible defaultOpen={[topics![0].levelLabel ?? '']} collapseStyle={'free'}>
                  {topics?.map((topicLevel, index) => {
                    const lessons: ILessonCardProps[] = topicLevel.topics!.map((topic, index) => {
                      const footer: ILessonCardFooterProps = { borderTop: true, backgroundColour: 'primary', text: 'View', icon: <ChevronIcon size={15} tone="contrast-text" />, colour: 'contrast-text' };
                      return {
                        assessmentState: topic.assessmentState!,
                        score: topic.score,
                        name: topic.name!,
                        thumbnailUrl: '',
                        description: topic.description,
                        lessonUrl: PagePath.article.replace(":topicId", topic.id!),
                        disabled: false,
                        completed: topic.assessmentState == AssessmentState.Assessed,
                        footer,
                        courseSlug: topicLevel.level!.toString(),
                        path: PagePath.skill.replace(":skillId", props.skillId!),
                      };
                    });

                    return (
                      <CollapsibleStep
                        key={topicLevel.levelLabel}
                        id={topicLevel.levelLabel!}
                        colour={randomColours[index % randomColours.length]}
                        title={topicLevel.levelLabel!}
                        tagContent={() => {
                          if (!topicLevel.topics?.length) return <React.Fragment />;
                          return (
                            <React.Fragment>
                              <h5 className="heading5">
                                {topicLevel.levelDesctiption}
                              </h5>
                            </React.Fragment>
                          );
                        }}
                      >
                        <div className="card">
                          <CollapsibleCardRow>
                            {lessons.map(lesson => (
                              <LessonCard key={lesson.name} {...lesson} />
                            ))}
                          </CollapsibleCardRow>
                        </div>
                      </CollapsibleStep>
                    );
                  })}
                </Collapsible>
              </CollapsibleContainer>)}
          </div>
        }
      </PageLoaderLayer>

      {/* variation */}
      <FocusPanel zIndex={103}
        open={cantFind} variation="standard" onClose={() => setCantFind(false)}>
        <form id="search-roles" onSubmit={handleSubmit(onSubmitHandler)}>
          <div className="dialogBodyLayout">
            <div className="card" style={{ position: 'relative', marginBottom: 30 }}>
              <div className="row">
                <div className="column">
                  <FormControl
                    control={control}
                    rules={new RequiredValidator('Enter a prompt')}
                    name="prompt"
                    render={({ field, fieldState }) => {
                      return <Textarea {...field} haserror={!!fieldState.error} label="What kind of topics would you be interested in related to this skill?" responsive />;
                    }}
                  />
                </div>
              </div>
              <Button
                fullWidthMobile
                type="button"
                square
                theme="dark"
              >
                <div style={{ display: 'flex', gap: 15, alignItems: 'center', justifyContent: 'center' }}>
                  Search
                  <SearchIcon />
                </div>
              </Button>
            </div>
          </div>
        </form>
      </FocusPanel>
    </React.Fragment>
  );
}
